import { 
    useId,
    useEffect, 
    useRef, 
    useState, 
    type MouseEvent 
} from "react";
import { 
    Component_Loading, 
    Component_User_Info, 
    type DropdownOption
} from "@eifu-development/endurien_react_components";
import { 
    Api_Consumer_Eifu, 
    type Eifu_Translation_Map, 
    type Eifu_User_Info, 
    type Settings_Api_Consumer_Eifu 
} from "@eifu-development/eifu-api-consumer";

import "@eifu-development/endurien_react_components/dist/endurien_react_components.css";

const Translations = {
    AddressLineOne: "strAddressLineOne",
    AddressLineTwo: "strAddressLineTwo",
    Cancel: "BtnCancel",
    City: "strCity",
    ContactInformation: "lblContact",
    Country: "strCountry",
    Email: "strEmail",
    Extension: "strContactInfoExtension",
    FaxNumber: "lblFaxNumber",
    FirstName: "strFirstName",
    LastName: "strLastName",
    PhoneNumber: "strPhone",
    ShippingAddress: "lblShippingAddress",
    State: "strState",
    PostalCode: "strZipCode",
    UpdateAndSave: "BtnSaveUpdate"
} as const;

const TranslationKeys = Object.values(Translations);

const EifuCss = {
    Button: "btn btn-default",
    Select: "radInput",
    InputTextLabel: "riSingle RadInput RadInput_Default",
    InputText: "riTextBox radInput"
};

interface Props_Console_User_Info {
    id: string;
    settings: Settings_Api_Consumer_Eifu;
}

const Console_User_Info: React.FC<Props_Console_User_Info> = ({
    id,
    settings
}) => {

    // Make sure ID has a value
    const idUnique = useId();
    id = id || `${idUnique}_eifu_console_user_info`;

    const [loading, setLoading] = useState<boolean>(false);
    const [translations, setTranslation] = useState<Eifu_Translation_Map>({});
    const [countries, setCountries] = useState<DropdownOption[]>([]);
    const [userInfo, setUserInfo] = useState<Eifu_User_Info>({});

    const apiConsumer = useRef(Api_Consumer_Eifu.getInstance(settings)).current;

    useEffect(() => {
        setLoading(true);
        const load = async () => {
            const resultsTranslations = await apiConsumer.translationMapFetch(TranslationKeys);
            setTranslation(resultsTranslations);

            const resultsCountries = await apiConsumer.countriesFetch();
            const countries = resultsCountries.reduce((acc, country) => {
                acc.push({ [country.CountryName]: country.CountryCode });
                return acc;
            }, [] as DropdownOption[])
            setCountries(countries);

            const resultsUserInfo = await apiConsumer.userInfoFetch();
            setUserInfo(resultsUserInfo);

            setLoading(false);
        }
        load();
    }, [apiConsumer]);

    const onInputChangeAddress = (key: string, value: string | null) => {
        setUserInfo(previousState => ({
            ...previousState,
            Address: { 
                ...previousState.Address,
                [key]: value 
            }
        }));
    };

    const onInputChangeContact = (key: string, value: string | null) => {
        setUserInfo(previousState => ({
            ...previousState,
            Contact: { 
                ...previousState.Contact,
                [key]: value 
            }
        }));
    };

    const onUpdateAndSave = (event: MouseEvent<EventTarget>) => {
        event.stopPropagation();
        setLoading(true);
        const save = async () => {
            await apiConsumer.userInfoSet(userInfo);
            setLoading(false);
        };
        save();
    };

    const onCancel = (event: MouseEvent<EventTarget>) => {
        event.stopPropagation();
        setLoading(true);
        const load = async () => {
            const resultsUserInfo = await apiConsumer.userInfoFetch();
            setUserInfo(resultsUserInfo);

            setLoading(false);
        };

        load();
    };

    if (loading)
        return <Component_Loading/>;

    return (
        <>
            <Component_User_Info
                id={id}
                settings={
                    {
                        address: {
                            fieldset: {
                                legend: translations[Translations.ShippingAddress]
                            },
                            firstName: {
                                value: userInfo?.Address?.FirstName,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeAddress("FirstName", newValue),
                                label: {
                                    text: translations[Translations.FirstName],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            lastName: {
                                value: userInfo?.Address?.LastName,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeAddress("LastName", newValue),
                                label: {
                                    text: translations[Translations.LastName],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            addressLineOne: {
                                value: userInfo?.Address?.AddressLineOne,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeAddress("AddressLineOne", newValue),
                                label: {
                                    text: translations[Translations.AddressLineOne],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            addressLineTwo: {
                                value: userInfo?.Address?.AddressLineTwo,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeAddress("AddressLineTwo", newValue),
                                label: {
                                    text: translations[Translations.AddressLineTwo],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            city: {
                                value: userInfo?.Address?.City,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeAddress("City", newValue),
                                label: {
                                    text: translations[Translations.City],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            state: {
                                value: userInfo?.Address?.State,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeAddress("State", newValue),
                                label: {
                                    text: translations[Translations.State],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            country: {
                                options: countries,
                                className: EifuCss.Select,
                                value: userInfo?.Address?.Country,
                                setValue: newValue => onInputChangeAddress("Country", newValue),
                                label: {
                                    text: translations[Translations.Country]
                                }
                            },
                            postalCode: {
                                value: userInfo?.Address?.PostalCode,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeAddress("PostalCode", newValue),
                                label: {
                                    text: translations[Translations.PostalCode],
                                    className: EifuCss.InputTextLabel,
                                }
                            }
                        },
                        contact: {
                            fieldset: {
                                legend: translations[Translations.ContactInformation]
                            },
                            phoneNumber: {
                                value: userInfo?.Contact?.PhoneNumber,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeContact("PhoneNumber", newValue),
                                label: {
                                    text: translations[Translations.PhoneNumber],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            extension: {
                                value: userInfo?.Contact?.Extension,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeContact("Extension", newValue),
                                label: {
                                    text: translations[Translations.Extension],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            faxNumber: {
                                value: userInfo?.Contact?.FaxNumber,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeContact("FaxNumber", newValue),
                                label: {
                                    text: translations[Translations.FaxNumber],
                                    className: EifuCss.InputTextLabel,
                                }
                            },
                            email: {
                                value: userInfo?.Contact?.Email,
                                className: EifuCss.InputText,
                                setValue: newValue => onInputChangeContact("Email", newValue),
                                label: {
                                    text: translations[Translations.Email],
                                    className: EifuCss.InputTextLabel,
                                }
                            }
                        },
                        updateAndSave: {
                            text: translations[Translations.UpdateAndSave],
                            className: EifuCss.Button,
                            onClick: onUpdateAndSave,
                        },
                        cancel: {
                            text: translations[Translations.Cancel],
                            className: EifuCss.Button,
                            onClick: onCancel
                        }
                    }
                }
            />
    
        </>
    );
};

export default Console_User_Info;